<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    appendTo="body"
    :base-z-index="1300"
    :closable="!saving"
    :dismissableMask="!saving"
    :draggable="false"
    :style="{ width: '28rem' }"
    :breakpoints="{ '960px': '90vw', '575px': '98vw' }"
    :pt="crcFormDialogPt"
    @hide="onClose"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        >
          <i class="pi pi-user text-base" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">
            {{ isEdit ? 'Edit User' : 'Add User' }}
          </div>
        </div>
      </div>
    </template>

    <form @submit.prevent="handleSubmit">
      <section class="crc-form-dialog-section">
        <div class="crc-form-dialog-fields">
          <FloatLabel variant="on" class="w-full">
            <InputText
              id="settings-user-fullname"
              v-model="draft.fullName"
              :disabled="saving"
              maxlength="160"
              autocomplete="off"
              :invalid="submitted && !draft.fullName?.trim()"
              class="w-full rounded-md"
            />
            <label for="settings-user-fullname" class="text-sm font-medium">
              Full name <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.fullName?.trim()" class="text-xs text-red-500 pl-1">
            Full name is required.
          </small>

          <FloatLabel variant="on" class="w-full">
            <InputText
              id="settings-user-email"
              v-model="draft.email"
              type="email"
              :disabled="saving"
              maxlength="190"
              autocomplete="off"
              :invalid="submitted && !draft.email?.trim()"
              class="w-full rounded-md"
            />
            <label for="settings-user-email" class="text-sm font-medium">
              Email <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.email?.trim()" class="text-xs text-red-500 pl-1">
            Email is required.
          </small>

          <FloatLabel variant="on" class="w-full">
            <InputText
              id="settings-user-username"
              v-model="draft.username"
              :disabled="saving"
              maxlength="64"
              autocomplete="off"
              :invalid="submitted && !draft.username?.trim()"
              class="w-full rounded-md"
            />
            <label for="settings-user-username" class="text-sm font-medium">
              Username <span class="text-red-500">*</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && !draft.username?.trim()" class="text-xs text-red-500 pl-1">
            Username is required.
          </small>

          <FloatLabel variant="on" class="w-full">
            <Select
              inputId="settings-user-role"
              v-model="draft.role"
              :options="roleOptions"
              optionLabel="label"
              optionValue="value"
              :disabled="saving"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="settings-user-role" class="text-sm font-medium">Role</label>
          </FloatLabel>

          <FloatLabel variant="on" class="w-full">
            <Password
              inputId="settings-user-password"
              v-model="draft.password"
              :disabled="saving"
              :feedback="false"
              toggleMask
              autocomplete="new-password"
              :invalid="submitted && passwordInvalid"
              class="w-full"
              inputClass="w-full rounded-md"
            />
            <label for="settings-user-password" class="text-sm font-medium">
              Password
              <span v-if="!isEdit" class="text-red-500">*</span>
              <span v-else class="text-surface-500 font-normal"> (leave blank to keep)</span>
            </label>
          </FloatLabel>
          <small v-if="submitted && passwordInvalid" class="text-xs text-red-500 pl-1">
            {{ passwordHint }}
          </small>
        </div>
      </section>

      <section class="crc-form-dialog-section">
        <div class="flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-medium">Active</div>
            <div class="text-xs text-surface-500">Disabled users cannot sign in</div>
          </div>
          <ToggleSwitch v-model="draft.active" :disabled="saving" />
        </div>
      </section>

      <div class="crc-form-dialog-footer">
        <div class="crc-form-dialog-footer__actions">
          <Button
            type="button"
            label="Cancel"
            severity="secondary"
            text
            :disabled="saving"
            @click="visibleProxy = false"
          />
          <Button type="submit" label="Save" icon="pi pi-check" :loading="saving" />
        </div>
      </div>
    </form>
  </Dialog>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import FloatLabel from 'primevue/floatlabel';
import ToggleSwitch from 'primevue/toggleswitch';
import Password from 'primevue/password';
import { crcFormDialogPt } from '@/components/common/dialog/crcDialogPt.js';
import { DRIVERS_ROLE_OPTIONS } from '../../utils/driversRoleLabels';

defineOptions({ name: 'UserEditModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  saving: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:visible', 'save']);

const roleOptions = DRIVERS_ROLE_OPTIONS;
const submitted = ref(false);

const draft = reactive({
  id: null,
  fullName: '',
  email: '',
  username: '',
  role: 'hr',
  password: '',
  active: true
});

const isEdit = computed(() => !!draft.id);

const passwordInvalid = computed(() => {
  if (!isEdit.value && !draft.password) return true;
  if (draft.password && draft.password.length < 8) return true;
  return false;
});

const passwordHint = computed(() => {
  if (!isEdit.value && !draft.password) return 'Password is required.';
  return 'Password must be at least 8 characters.';
});

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

function resetDraft() {
  draft.id = null;
  draft.fullName = '';
  draft.email = '';
  draft.username = '';
  draft.role = 'hr';
  draft.password = '';
  draft.active = true;
  submitted.value = false;
}

function openCreate() {
  resetDraft();
  visibleProxy.value = true;
}

function openEdit(user) {
  resetDraft();
  draft.id = user?.id ?? null;
  draft.fullName = user?.fullName || '';
  draft.email = user?.email || '';
  draft.username = user?.username || '';
  draft.role = user?.role || 'hr';
  draft.active = user?.active !== false;
  draft.password = '';
  visibleProxy.value = true;
}

function onClose() {
  resetDraft();
}

function handleSubmit() {
  submitted.value = true;
  if (!draft.fullName?.trim() || !draft.email?.trim() || !draft.username?.trim()) return;
  if (passwordInvalid.value) return;

  const payload = {
    id: draft.id || undefined,
    fullName: draft.fullName.trim(),
    email: draft.email.trim(),
    username: draft.username.trim(),
    role: draft.role,
    active: !!draft.active
  };
  if (draft.password) {
    payload.password = draft.password;
  }
  emit('save', payload);
}

defineExpose({ openCreate, openEdit });
</script>
